import { AxiosError } from "axios";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { getPracticeSetUrl } from "adminComponents/molecules/LibraryPracticeSetCard";
import { ICoverImage } from "adminComponents/molecules/PracticeSetCoverImagePicker";
import { CreatePracticeSetFlyout } from "adminComponents/organisms/CreatePracticeSetFlyout";
import { FormData } from "adminComponents/organisms/PracticeSetDetailFlyout";
import type { IGenerateState as ISmartSetGenerateState } from "adminComponents/organisms/SmartSetFlyout";
import { useErrorToast, useShowToast } from "adminComponents/utils/toast";
import {
  emptyPracticeSet,
  emptyPracticeSetGradeLevel,
  emptyPracticeSetSubject,
} from "lib/empty";
import { useAuth } from "lib/features/auth";
import { useFetchClassrooms } from "lib/features/classrooms";
import {
  useFetchGroupsSmartSetsCapabilities,
  useGenerateGroupSmartSetPreview,
  useMutatePracticeSet,
} from "lib/features/practiceSets";
import {
  IMutatePracticeSetArgs,
  IMutatePracticeSetResponse,
} from "lib/features/practiceSets/useMutatePracticeSet";
import { useHubSpot } from "lib/hooks/useHubSpot";
import { langLabelMap, supportedLangs } from "lib/lang";
import {
  AnalyticsEvent,
  CoverImageBGColorSchemeType,
  CoverImageBGPatternType,
  CoverImageIconType,
  ICollection,
  IGradeLevel,
  IPracticeSet,
  IPracticeSetType,
  ISmartSetSettings,
  ISubject,
  PracticeSetStatus,
  SmartSetType,
  UserRole,
} from "lib/types";

export interface IArgs {
  collections: Array<ICollection>;
  subjects: Array<ISubject>;
  gradeLevels: Array<IGradeLevel>;
  practiceSetType?: IPracticeSetType;
  onOpen?: () => void;
  onSubmit?: (data: IMutatePracticeSetArgs) => void;
}

export interface IResult {
  handleCreate: (collection?: ICollection) => void;
  flyout: React.ReactElement;
}

interface ICustomSetState {
  coverImage?: ICoverImage;
}

interface IFlyoutState {
  isOpen: boolean;
  initialCollectionId?: string;
}

interface ISmartSetState {
  defaultValue?: {
    smartSetType?: SmartSetType;
    smartSetSettings?: ISmartSetSettings;
  };
  generateState: ISmartSetGenerateState;
}

const defaultCoverImage = {
  bgColorScheme: "LIGHT_BLUE" as CoverImageBGColorSchemeType,
  bgPattern: "OTHER_FUN_PARTY" as CoverImageBGPatternType,
  icon: "OTHER_TRIVIA" as CoverImageIconType,
};

export const useCreatePracticeSet = ({
  subjects,
  gradeLevels,
  collections,
  practiceSetType,
  onOpen,
  onSubmit,
}: IArgs): IResult => {
  const [flyoutState, setFlyoutState] = useState<IFlyoutState>({
    isOpen: false,
  });
  const [customSetState, setCustomSetState] = useState<ICustomSetState>({});
  const [smartSetState, setSmartSetState] = useState<ISmartSetState>({
    defaultValue: undefined,
    generateState: {
      isLoading: false,
      isError: false,
      isSuccess: false,
    },
  });
  const history = useHistory();
  const showToast = useShowToast();
  const { t } = useTranslation("admin", {
    useSuspense: false,
    keyPrefix: "createPracticeSetFlyout",
  });
  const { authUser, hasNoPremiumAccess } = useAuth();
  const { trackHubSpotEvent } = useHubSpot();
  const fetchClassrooms = useFetchClassrooms({ disabled: authUser?.is_guest });
  const fetchSmartSetCapabilities = useFetchGroupsSmartSetsCapabilities({
    enabled: !practiceSetType || practiceSetType === IPracticeSetType.Smart,
  });

  const onSuccess = (data: IMutatePracticeSetResponse) => {
    history.push(getPracticeSetUrl(data.practice_set, authUser));
    showToast(t("successToast"));
    handleClose();

    trackHubSpotEvent(AnalyticsEvent.HubSpot_CreatedPracticeSet, {});
  };

  const mutatePracticeSet = useMutatePracticeSet({ onSuccess });

  useErrorToast(mutatePracticeSet.error);

  const handleCreate = (collection?: ICollection) => {
    setFlyoutState({ isOpen: true, initialCollectionId: collection?.id });

    onOpen?.();
  };

  const handleClose = () => {
    setFlyoutState({ isOpen: false, initialCollectionId: undefined });
    setCustomSetState({});
    setSmartSetState({
      defaultValue: undefined,
      generateState: {
        isLoading: false,
        isError: false,
        isSuccess: false,
      },
    });
  };

  const handleSubmit = useCallback(
    (data: IMutatePracticeSetArgs) => {
      mutatePracticeSet.mutate(data);

      onSubmit?.(data);
    },
    [mutatePracticeSet, onSubmit]
  );

  const typeSelection = useMemo(() => {
    const canCreateSmartSet =
      fetchSmartSetCapabilities.data?.groups.some(
        (gc) =>
          gc.capabilities.filter((capability) => capability.is_capable).length >
          0
      ) ?? false;

    return {
      isLoadingCapabilities: fetchSmartSetCapabilities.isLoading,
      canCreateSmartSet,
      hasNoPremiumAccess: hasNoPremiumAccess,
    };
  }, [
    fetchSmartSetCapabilities.data,
    fetchSmartSetCapabilities.isLoading,
    hasNoPremiumAccess,
  ]);

  /** Custom Set Handlers & Data */

  const handleSaveCustomSet = useCallback(
    (data: FormData) => {
      const {
        title,
        description,
        folder,
        subjects,
        gradeLevels,
        language,
        availability,
        cncFamilyCode,
        cncCode,
        isCertified,
        isPremium,
        smartSetType,
      } = data.form;

      const { bgColorScheme, bgPattern, icon } =
        customSetState.coverImage ?? defaultCoverImage;

      handleSubmit({
        id: undefined,
        title,
        description,
        collection_id: folder,
        status: PracticeSetStatus.Active,
        availability,
        language_code: language,
        grade_levels: gradeLevels.map((id) => ({
          grade_level: { id },
        })),
        subjects: subjects.map((id) => ({ subject: { id } })),
        reference_materials: [],
        cnc_family_code: cncFamilyCode || "",
        cnc_code: cncCode || "",
        is_certified: isCertified || false,
        is_premium: isPremium || false,
        cover_image_bg_color_scheme: bgColorScheme,
        cover_image_bg_pattern: bgPattern,
        cover_image_icon: icon,
        smart_set_type: smartSetType,
      });
    },
    [handleSubmit, customSetState.coverImage]
  );

  const handleCoverImageChange = useCallback((coverImage: ICoverImage) => {
    setCustomSetState({ coverImage });
  }, []);

  const customSet = useMemo(() => {
    const customSetOptions = {
      subjects: subjects.map((s) => ({
        value: s.id,
        label: s.parent_id !== "0" ? `${s.parent_name}: ${s.name}` : s.name,
      })),
      gradeLevels: gradeLevels.map((g) => ({
        value: g.id,
        label: g.grade_level,
      })),
      languages: supportedLangs.map((l) => ({
        value: l,
        label: langLabelMap[l],
      })),
      folders: collections.map((c) => ({
        label: c.name,
        value: c.id,
      })),
    };

    return {
      isLoading: mutatePracticeSet.isLoading,
      handleCoverImageChange,
      handleSubmit: handleSaveCustomSet,
      options: customSetOptions,
      showCncFields: authUser?.role === UserRole.ContentSpecialist,
      allowPublish: true,
      initialCollectionId: flyoutState.initialCollectionId,
    };
  }, [
    mutatePracticeSet.isLoading,
    flyoutState.initialCollectionId,
    authUser?.role,
    subjects,
    gradeLevels,
    collections,
    handleSaveCustomSet,
    handleCoverImageChange,
  ]);

  /** Smart Set Handlers & Data */

  const generateGroupSmartSetPreview = useGenerateGroupSmartSetPreview({
    onSuccess: (data) => {
      setSmartSetState((s) => ({
        ...s,
        generateState: {
          isLoading: false,
          isError: false,
          isSuccess: true,
          items: data.practice_set_items,
          practiceSet: s.generateState.practiceSet,
        },
      }));
    },
    onError: (error: AxiosError) => {
      const status = error.response?.status || 0;

      let message: string | undefined;
      if (status >= 400 && status < 500) {
        message = (error.response?.data as { message?: string }).message;
      }

      setSmartSetState((s) => ({
        ...s,
        generateState: {
          isLoading: false,
          isError: true,
          isSuccess: false,
          errorMessage: message || t("errorGeneratingSmartSet"),
        },
      }));
    },
  });

  const handleSaveSmartSet = useCallback(() => {
    if (!smartSetState.generateState.practiceSet) return;

    const { grade_levels, subjects } = smartSetState.generateState.practiceSet;

    handleSubmit({
      ...smartSetState.generateState.practiceSet,
      id: undefined,
      grade_levels: grade_levels.map((g) => ({
        grade_level: { id: g.grade_level.id },
      })),
      subjects: subjects.map((s) => ({ subject: { id: s.subject.id } })),
      smart_set_settings: smartSetState.defaultValue?.smartSetSettings,
      smart_set_type: smartSetState.defaultValue?.smartSetType,
      practice_set_items: smartSetState.generateState.items,
    });
  }, [smartSetState.defaultValue, smartSetState.generateState, handleSubmit]);

  const handleGenerateSmartSet = useCallback(
    ({
      smartSetSettings,
      smartSetType,
    }: {
      smartSetType: SmartSetType;
      smartSetSettings: ISmartSetSettings;
    }) => {
      if (!smartSetSettings.group_id) return;

      const classroom = fetchClassrooms.data?.classrooms.find(
        (c) => c.id === smartSetSettings.group_id
      );

      if (!classroom) return;

      const gradeLevels = [
        ...(classroom?.group_grade_levels || []).map((gradeLevel) => {
          return {
            ...emptyPracticeSetGradeLevel,
            grade_level: {
              ...emptyPracticeSetGradeLevel.grade_level,
              id: gradeLevel.grade_level_id,
            },
          };
        }),
      ];

      const dateFormat = "MMMM D, YYYY";

      let practiceSet: IPracticeSet | undefined = undefined;

      switch (smartSetType) {
        case SmartSetType.Review: {
          const startDate = moment(smartSetSettings.start_time).format(
            dateFormat
          );
          const endDate = moment(smartSetSettings.end_time).format(dateFormat);
          practiceSet = {
            ...emptyPracticeSet,
            grade_levels: gradeLevels,
            title: t("defaultSmartSetReviewTitle", {
              classroom_name: classroom.name,
              start_date: startDate,
              end_date: endDate,
            }),
            description: t("defaultSmartSetReviewDescription", {
              classroom_name: classroom.name,
              start_date: startDate,
              end_date: endDate,
            }),
            smart_set_type: SmartSetType.Review,
            cover_image_bg_pattern: "SMART_SET",
            collection_id: flyoutState.initialCollectionId ?? "0",
            reference_materials: [],
            cnc_family_code: "",
            cnc_code: "",
            is_certified: false,
            is_premium: false,
            status: PracticeSetStatus.Active,
          };
          break;
        }
        case SmartSetType.StandardsIntervention: {
          const subjectIds = smartSetSettings.subject_ids;
          const subjectsList = subjectIds
            ?.map(
              (subjectId) =>
                subjects.find((subject) => subject.id === subjectId)?.name
            )
            .filter((a) => !!a)
            .join(", ");
          const currentDate = moment().format(dateFormat);

          practiceSet = {
            ...emptyPracticeSet,
            subjects: [
              ...(subjectIds || []).map((subjectId) => {
                return {
                  ...emptyPracticeSetSubject,
                  subject: {
                    ...emptyPracticeSetSubject.subject,
                    id: subjectId,
                  },
                };
              }),
            ],
            grade_levels: gradeLevels,
            title: t("defaultSmartSetStandardsInterventionTitle", {
              classroom_name: classroom.name,
              subjects: subjectsList,
            }),
            description: t("defaultSmartSetStandardsInterventionDescription", {
              classroom_name: classroom.name,
              subjects: subjectsList,
              current_date: currentDate,
            }),
            smart_set_type: SmartSetType.StandardsIntervention,
            cover_image_bg_pattern: "SMART_SET",
            collection_id: flyoutState.initialCollectionId ?? "0",
          };
          break;
        }
      }

      if (!practiceSet) return;

      setSmartSetState({
        defaultValue: {
          smartSetType,
          smartSetSettings,
        },
        generateState: {
          isLoading: true,
          isError: false,
          isSuccess: false,
          practiceSet,
        },
      });

      generateGroupSmartSetPreview.mutate({
        smart_set_settings: smartSetSettings,
        smart_set_type: smartSetType,
      });
    },
    [
      t,
      fetchClassrooms.data,
      subjects,
      flyoutState.initialCollectionId,
      generateGroupSmartSetPreview,
    ]
  );

  const handleRestartSmartSet = useCallback(() => {
    setSmartSetState((s) => ({
      defaultValue: s.defaultValue,
      generateState: {
        isLoading: false,
        isError: false,
        isSuccess: false,
      },
    }));
  }, []);

  const smartSet = useMemo(() => {
    return {
      classrooms: fetchClassrooms.data?.classrooms ?? [],
      isSaving: mutatePracticeSet.isLoading,
      groupSmartSetsCapabilities: fetchSmartSetCapabilities.data?.groups ?? [],
      subjects,
      handleSave: handleSaveSmartSet,
      handleGenerate: handleGenerateSmartSet,
      handleRestart: handleRestartSmartSet,
      generateState: smartSetState.generateState,
      defaultValue: smartSetState.defaultValue,
    };
  }, [
    fetchClassrooms.data,
    mutatePracticeSet.isLoading,
    smartSetState,
    subjects,
    handleSaveSmartSet,
    handleGenerateSmartSet,
    handleRestartSmartSet,
    fetchSmartSetCapabilities.data,
  ]);

  /** Instant Set Handlers & Data */

  const flyout = (
    <CreatePracticeSetFlyout
      isOpen={flyoutState.isOpen}
      handleClose={handleClose}
      customSet={customSet}
      typeSelection={typeSelection}
      smartSet={smartSet}
      practiceSetType={practiceSetType}
    />
  );

  return {
    flyout,
    handleCreate,
  };
};
